import React, { useState, useEffect, useCallback } from 'react';
import { invPOService, invItemService, invSupplierService, invUnitService } from '../../services/inventoryService';
import { card, pageWrap, btn, inputStyle, selectStyle, label, fieldGroup, modal, modalBox,
  sectionTitle, errorBox, emptyState, statusBadge, fmtCost, fmtDate, friendlyErr } from './inventoryStyles';

const PO_STATUSES = ['draft','sent','part_received','received','cancelled'];
const STATUS_LABELS = { draft: 'Draft', sent: 'Sent', part_received: 'Part Received', received: 'Received', cancelled: 'Cancelled' };

const PurchaseOrders = ({ restaurant, onNavigate }) => {
  const [pos, setPos]             = useState([]);
  const [items, setItems]         = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [units, setUnits]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm]   = useState(false);
  const [viewPO, setViewPO]       = useState(null);
  const [form, setForm]           = useState({ supplier_id: '', order_date: '', expected_delivery_date: '', notes: '', tax_amount: '0' });
  const [lineItems, setLineItems] = useState([]);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const restaurantId = restaurant?.id;

  const load = useCallback(async () => {
    setLoading(true);
    const [pRes, iRes, sRes, uRes] = await Promise.all([
      invPOService.getAll(restaurantId),
      invItemService.getAll(restaurantId),
      invSupplierService.getAll(restaurantId),
      invUnitService.getAll(restaurantId),
    ]);
    setPos(pRes.data || []);
    setItems((iRes.data || []).filter(i => i.is_active));
    setSuppliers((sRes.data || []).filter(s => s.is_active));
    setUnits(uRes.data || []);
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ supplier_id: '', order_date: new Date().toISOString().slice(0, 10), expected_delivery_date: '', notes: '', tax_amount: '0' });
    setLineItems([{ inventory_item_id: '', ordered_qty: '', purchase_unit_id: '', unit_price: '', line_total: '0' }]);
    setError(''); setShowForm(true);
  };

  const addLine = () => setLineItems(p => [...p, { inventory_item_id: '', ordered_qty: '', purchase_unit_id: '', unit_price: '', line_total: '0' }]);
  const removeLine = (i) => setLineItems(p => p.filter((_, idx) => idx !== i));
  const updateLine = (idx, field, val) => {
    setLineItems(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const updated = { ...l, [field]: val };
      if (field === 'ordered_qty' || field === 'unit_price') {
        const qty = parseFloat(field === 'ordered_qty' ? val : updated.ordered_qty) || 0;
        const price = parseFloat(field === 'unit_price' ? val : updated.unit_price) || 0;
        updated.line_total = (qty * price).toFixed(2);
      }
      return updated;
    }));
  };

  const subtotal = lineItems.reduce((s, l) => s + (parseFloat(l.line_total) || 0), 0);
  const total = subtotal + (parseFloat(form.tax_amount) || 0);

  const save = async () => {
    if (lineItems.length === 0 || lineItems.some(l => !l.inventory_item_id || !l.ordered_qty)) {
      setError('Add at least one line item with item and quantity.'); return;
    }
    setSaving(true); setError('');
    const { error: e } = await invPOService.create(restaurantId, form, lineItems);
    setSaving(false);
    if (e) { setError(friendlyErr(e)); return; }
    setShowForm(false); load();
  };

  const updateStatus = async (po, status) => {
    if (!window.confirm(`Mark PO ${po.po_number} as "${STATUS_LABELS[status]}"?`)) return;
    await invPOService.updateStatus(po.id, status);
    if (status === 'received' || status === 'part_received') {
      onNavigate && onNavigate('stock-inward', { po_id: po.id });
    }
    load();
  };

  const filtered = pos.filter(p => !filterStatus || p.status === filterStatus);

  return (
    <div style={pageWrap}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#111' }}>Purchase Orders</h2>
            <p style={{ margin: '4px 0 0', color: '#888', fontSize: '0.88rem' }}>{pos.filter(p => ['draft','sent'].includes(p.status)).length} pending POs</p>
          </div>
          <button style={btn('primary')} onClick={openNew}>+ Create PO</button>
        </div>

        <div style={{ ...card, padding: '14px 20px', marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...selectStyle(), width: '180px', padding: '8px 12px' }}>
            <option value="">All Statuses</option>
            {PO_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>

        <div style={{ ...card, overflow: 'hidden' }}>
          {loading ? (
            <div style={emptyState}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={emptyState}>
              <div style={{ fontWeight: '600', color: '#555', marginBottom: '8px' }}>No purchase orders yet</div>
              <div>Create your first PO to track inward stock.</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #f3f4f6' }}>
                  {['PO Number', 'Supplier', 'Order Date', 'Expected', 'Items', 'Amount', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(po => (
                  <tr key={po.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 14px', fontWeight: '700', color: '#333', fontFamily: 'monospace' }}>{po.po_number}</td>
                    <td style={{ padding: '12px 14px', color: '#555' }}>{po.supplier?.name || '—'}</td>
                    <td style={{ padding: '12px 14px', color: '#555' }}>{fmtDate(po.order_date)}</td>
                    <td style={{ padding: '12px 14px', color: '#555' }}>{fmtDate(po.expected_delivery_date)}</td>
                    <td style={{ padding: '12px 14px', color: '#555' }}>{po.items?.length || 0}</td>
                    <td style={{ padding: '12px 14px', fontWeight: '700' }}>{fmtCost(po.total_amount)}</td>
                    <td style={{ padding: '12px 14px' }}><span style={statusBadge(po.status)}>{STATUS_LABELS[po.status]}</span></td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button style={{ ...btn('outline'), padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => setViewPO(po)}>View</button>
                        {po.status === 'draft' && (
                          <button style={{ ...btn('orange'), padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => updateStatus(po, 'sent')}>Mark Sent</button>
                        )}
                        {['draft','sent','part_received'].includes(po.status) && (
                          <button style={{ ...btn('green'), padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => onNavigate && onNavigate('inward', { po })}>Receive</button>
                        )}
                        {['draft','sent'].includes(po.status) && (
                          <button style={{ ...btn('danger'), padding: '5px 10px', fontSize: '0.78rem' }} onClick={() => updateStatus(po, 'cancelled')}>Cancel</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create PO Modal */}
      {showForm && (
        <div style={modal} onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <div style={{ ...modalBox, maxWidth: '780px' }}>
            <h3 style={{ ...sectionTitle, marginBottom: '20px' }}>Create Purchase Order</h3>
            {error && <div style={errorBox}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <div style={fieldGroup}>
                <label style={label}>Supplier</label>
                <select style={selectStyle()} value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}>
                  <option value="">Select supplier (optional)</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={label}>Order Date</label>
                <input style={inputStyle()} type="date" value={form.order_date} onChange={e => setForm(f => ({ ...f, order_date: e.target.value }))} />
              </div>
              <div style={fieldGroup}>
                <label style={label}>Expected Delivery</label>
                <input style={inputStyle()} type="date" value={form.expected_delivery_date} onChange={e => setForm(f => ({ ...f, expected_delivery_date: e.target.value }))} />
              </div>
              <div style={fieldGroup}>
                <label style={label}>Tax Amount (₹)</label>
                <input style={inputStyle()} type="number" min="0" step="0.01" value={form.tax_amount} onChange={e => setForm(f => ({ ...f, tax_amount: e.target.value }))} />
              </div>
              <div style={{ ...fieldGroup, gridColumn: '1 / -1' }}>
                <label style={label}>Notes</label>
                <input style={inputStyle()} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
              </div>
            </div>

            {/* Line Items */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ ...label, margin: 0 }}>Items *</label>
                <button style={{ ...btn('outline'), padding: '5px 12px', fontSize: '0.8rem' }} onClick={addLine}>+ Add Item</button>
              </div>
              {lineItems.map((line, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '8px', marginBottom: '8px', alignItems: 'end' }}>
                  <div>
                    {idx === 0 && <label style={{ ...label, marginBottom: '4px', fontSize: '0.78rem' }}>Item</label>}
                    <select style={selectStyle(!line.inventory_item_id)} value={line.inventory_item_id} onChange={e => updateLine(idx, 'inventory_item_id', e.target.value)}>
                      <option value="">Select item…</option>
                      {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                  </div>
                  <div>
                    {idx === 0 && <label style={{ ...label, marginBottom: '4px', fontSize: '0.78rem' }}>Qty</label>}
                    <input style={inputStyle(!line.ordered_qty)} type="number" min="0.001" step="0.001" value={line.ordered_qty} onChange={e => updateLine(idx, 'ordered_qty', e.target.value)} placeholder="0" />
                  </div>
                  <div>
                    {idx === 0 && <label style={{ ...label, marginBottom: '4px', fontSize: '0.78rem' }}>Unit</label>}
                    <select style={selectStyle()} value={line.purchase_unit_id} onChange={e => updateLine(idx, 'purchase_unit_id', e.target.value)}>
                      <option value="">Base unit</option>
                      {units.map(u => <option key={u.id} value={u.id}>{u.short_code}</option>)}
                    </select>
                  </div>
                  <div>
                    {idx === 0 && <label style={{ ...label, marginBottom: '4px', fontSize: '0.78rem' }}>Unit Price</label>}
                    <input style={inputStyle()} type="number" min="0" step="0.01" value={line.unit_price} onChange={e => updateLine(idx, 'unit_price', e.target.value)} placeholder="₹0" />
                  </div>
                  <button style={{ ...btn('danger'), padding: '8px 10px', fontSize: '0.82rem', alignSelf: 'flex-end' }} onClick={() => removeLine(idx)}>✕</button>
                </div>
              ))}
              <div style={{ textAlign: 'right', marginTop: '12px', padding: '12px', background: '#f9fafb', borderRadius: '10px', fontSize: '0.9rem' }}>
                <span style={{ color: '#555' }}>Subtotal: <strong>{fmtCost(subtotal)}</strong></span>
                <span style={{ marginLeft: '20px', color: '#555' }}>Tax: <strong>{fmtCost(parseFloat(form.tax_amount) || 0)}</strong></span>
                <span style={{ marginLeft: '20px', fontSize: '1rem', fontWeight: '800', color: '#111' }}>Total: {fmtCost(total)}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={btn('outline')} onClick={() => setShowForm(false)}>Cancel</button>
              <button style={btn('primary', saving)} onClick={save} disabled={saving}>{saving ? 'Creating…' : 'Create PO'}</button>
            </div>
          </div>
        </div>
      )}

      {/* View PO Modal */}
      {viewPO && (
        <div style={modal} onClick={e => { if (e.target === e.currentTarget) setViewPO(null); }}>
          <div style={{ ...modalBox, maxWidth: '640px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h3 style={{ ...sectionTitle, margin: 0 }}>{viewPO.po_number}</h3>
                <div style={{ marginTop: '6px' }}><span style={statusBadge(viewPO.status)}>{STATUS_LABELS[viewPO.status]}</span></div>
              </div>
              <button style={{ ...btn('outline'), padding: '6px 14px', fontSize: '0.85rem' }} onClick={() => setViewPO(null)}>Close</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px', fontSize: '0.88rem' }}>
              <div><span style={{ color: '#888' }}>Supplier:</span> <strong>{viewPO.supplier?.name || '—'}</strong></div>
              <div><span style={{ color: '#888' }}>Order Date:</span> <strong>{fmtDate(viewPO.order_date)}</strong></div>
              <div><span style={{ color: '#888' }}>Expected:</span> <strong>{fmtDate(viewPO.expected_delivery_date)}</strong></div>
              <div><span style={{ color: '#888' }}>Total:</span> <strong style={{ color: '#16a34a' }}>{fmtCost(viewPO.total_amount)}</strong></div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '16px' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {['Item', 'Ordered', 'Received', 'Unit Price', 'Total'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '700', color: '#555', fontSize: '0.78rem' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(viewPO.items || []).map((line, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontWeight: '600' }}>{line.inventory_item?.name}</td>
                    <td style={{ padding: '10px 12px' }}>{line.ordered_qty} {line.purchase_unit?.short_code || ''}</td>
                    <td style={{ padding: '10px 12px', color: line.received_qty >= line.ordered_qty ? '#16a34a' : '#c2410c' }}>{line.received_qty || 0}</td>
                    <td style={{ padding: '10px 12px' }}>{fmtCost(line.unit_price)}</td>
                    <td style={{ padding: '10px 12px', fontWeight: '600' }}>{fmtCost(line.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {viewPO.notes && <div style={{ color: '#666', fontSize: '0.85rem' }}>Notes: {viewPO.notes}</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrders;
